import React from 'react'
import { Trans } from '@lingui/react/macro'
import { useLingui } from '@lingui/react/macro'
import { XStack, YStack } from 'tamagui'

import { Card } from '@newComponents/Card'
import { Icon } from '@newComponents/Icon'
import { Paragraph } from '@newComponents/Text'
import { Link } from '@newComponents/Link'

import { DocSection } from '../shared/DocSection'
import { DocScreenshot } from '../shared/DocScreenshot'

const CommandMenu = () => {
  const { t } = useLingui()

  return (
    <>
      <Card gap='$4'>
        <DocSection id='opening-the-command-menu' title={t`Opening the Command Palette`}>
          <Paragraph size='$5'>
            <Trans>
              The Command Palette is Guild's primary action center. It is the single place
              where you create Guilds, Events, and Presentations — there are no separate
              "Create" buttons elsewhere in the interface. It also serves as the entry
              point for{' '}
              <Link href='/docs/getting-started/search'>Search</Link>.
            </Trans>
          </Paragraph>
          <XStack gap='$3' alignItems='center'>
            <Icon name='search' size='$5' color='$color' flexShrink={0} />
            <Paragraph size='$5'>
              <Trans>
                Look for the magnifying glass icon in the top navigation bar. Click
                it to open the Command Palette. You can also open it with a keyboard
                shortcut for faster access.
              </Trans>
            </Paragraph>
          </XStack>
          <Paragraph size='$5'>
            <Trans>
              When the Command Palette opens, you will see a search input at the top and
              a list of quick actions below it. The menu also displays contextual
              suggestions based on the page you are currently viewing.
            </Trans>
          </Paragraph>
          <DocScreenshot
            src='docs/getting-started/command-menu-open.png'
            alt='Command Palette open showing search input and quick actions: Create an Event, Create a Presentation, Create a Guild, with contextual search suggestions below'
            caption={t`The Command Palette with quick actions and contextual suggestions`}
          />
        </DocSection>
      </Card>

      <Card gap='$4'>
        <DocSection id='quick-actions' title={t`Quick Actions`}>
          <Paragraph size='$5'>
            <Trans>
              The Command Palette presents three quick actions that let you create
              content immediately:
            </Trans>
          </Paragraph>
          <YStack gap='$3'>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='calendar-add' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>Create an Event</Paragraph> —
                  Start a new Event for one of your Guilds. You will choose the host
                  Guild, set the date and venue, and publish it for attendees to RSVP.
                  See{' '}
                  <Link href='/docs/events/creation'>Creating an Event</Link> for the
                  full walkthrough.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='projection-screen' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>Create a Presentation</Paragraph>{' '}
                  — Add a new Presentation to share a talk, workshop, or resource with
                  your community. See{' '}
                  <Link href='/docs/presentations/creation'>Creating a Presentation</Link>{' '}
                  for details.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='people-community' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>Create a Guild</Paragraph> — Set
                  up a new community from scratch. You will enter a name, description, and
                  photos to get started. See{' '}
                  <Link href='/docs/guilds/creation'>Creating a Guild</Link> for the full
                  walkthrough.
                </Trans>
              </Paragraph>
            </XStack>
          </YStack>
          <Paragraph size='$5'>
            <Trans>
              These quick actions are the only way to create these entities on Guild.
              If you are looking for a "Create" button on a Guild page or an Event
              listing, head to the Command Palette instead.
            </Trans>
          </Paragraph>
        </DocSection>
      </Card>

      <Card gap='$4'>
        <DocSection id='contextual-suggestions' title={t`Contextual Suggestions`}>
          <Paragraph size='$5'>
            <Trans>
              Below the quick actions, the Command Palette shows contextual suggestions
              that adapt to where you are on the platform. For example, when you are
              viewing a Guild page, you may see suggestions related to that Guild's
              Events or Members. These suggestions help you take action faster without
              leaving your current context.
            </Trans>
          </Paragraph>
          <Paragraph size='$5'>
            <Trans>
              You can also type into the search input to find anything on Guild. The
              search is AI-powered and understands natural language — try queries like
              "events about React" or "guilds in London". See{' '}
              <Link href='/docs/getting-started/search'>Search</Link> to learn more
              about what you can search for.
            </Trans>
          </Paragraph>
        </DocSection>
      </Card>
    </>
  )
}

export default CommandMenu
